import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SyntaxHighlighter, { createElement } from 'react-syntax-highlighter';
import { CodeIssues } from './CodeIssues';
import { Pre } from './PreTag';
const knownExtensions = {
    java: 'java',
    jbuilder: 'ruby',
    js: 'javascript',
    php: 'php',
    rb: 'ruby',
    ru: 'ruby',
    ruby: 'ruby',
    sass: 'scss',
    scss: 'scss',
    yaml: 'yaml',
    yml: 'yaml',
};
const knownFilenames = {
    gemfile: 'ruby',
    rakefile: 'ruby',
};
const lineNumberStyle = (messages, lineNumber, lineHeights) => {
    const issues = messages[lineNumber - 1];
    const measuredHeight = lineNumber in lineHeights.current ? lineHeights.current[lineNumber] : 0;
    if (issues && issues.length > 0) {
        return {
            display: 'block',
            marginBottom: measuredHeight,
        };
    }
    return {};
};
const renderer = (rows, stylesheet, useInlineStyles, block, blockIndex, expandedIssues, filePath, messageCount, toggleIssueDocs) => {
    // we want to display also errors in empty files,
    // so we need to iterate over something on the lines bellow this assignment
    if (rows.length === 0 && messageCount > 0) {
        rows = [
            {
                type: 'element',
                tagName: 'span',
                properties: {
                    id: filePath + ':1',
                    className: [],
                },
                children: [],
            },
        ];
    }
    return rows.map((node, rowIndex) => {
        const message = block.messages[block.startLine + rowIndex];
        const showCodeIssue = message && message.length !== 0;
        return _jsxs("div", { className: 'report-detail__code-line', children: [createElement({
                    node,
                    stylesheet,
                    useInlineStyles,
                    key: `code-segement${rowIndex}`,
                }), showCodeIssue ? _jsx(CodeIssues, { blockIndex: blockIndex, rowIndex: rowIndex, startLine: block.startLine, messages: block.messages, expandedIssues: expandedIssues, toggleIssueDocs: toggleIssueDocs }) : null] }, rowIndex);
    });
};
const lineProps = (filePath, block, lineNumber, coverageTitle, lineHeights, forceUpdate, coveredLines) => {
    const issues = block.messages[block.startLine + lineNumber - 1];
    const lineCoverage = coveredLines?.[block.startLine + lineNumber];
    const props = {
        id: filePath + ':' + (block.startLine + lineNumber),
        ...(typeof lineCoverage !== 'undefined' ? {
            className: lineCoverage > 0 ? 'covered' : 'not-covered',
            title: coverageTitle,
        } : {}),
    };
    const messagesCount = Object.values(block.messages).filter((message) => message && message.length > 0).length;
    const absoluteLineNumber = block.startLine + lineNumber;
    const handleMargin = (height) => {
        if (messagesCount === 1 && block.startLine === 0 && block.startLine === block.endLine) {
            return 0;
        }
        else {
            return height;
        }
    };
    if (issues && issues.length > 0) {
        return {
            ...props,
            ref: (element) => {
                if (element) {
                    // @ts-expect-error TS2339: Property 'offsetHeight' does not exist on type 'Element'.
                    const height = element.nextElementSibling?.offsetHeight;
                    if (lineHeights.current[absoluteLineNumber] !== height) {
                        lineHeights.current[absoluteLineNumber] = height;
                        forceUpdate();
                    }
                }
            },
            className: `highlighted${props.className ? ' ' + props.className : ''}`,
            style: {
                marginBottom: absoluteLineNumber in lineHeights.current ? handleMargin(lineHeights.current[absoluteLineNumber]) : 0,
                display: 'block',
            },
        };
    }
    return props;
};
const realStyle = (expandedIssues, blocks, index) => {
    let style = {};
    let blockIndex;
    let nodeIndex;
    for (const blockId of Object.keys(expandedIssues)) {
        [blockIndex, nodeIndex] = blockId.split('#');
        blockIndex = parseInt(blockIndex);
        nodeIndex = parseInt(nodeIndex);
        if (blockIndex === index &&
            expandedIssues[blockId].length &&
            nodeIndex + 1 + blocks[blockIndex].startLine > blocks[blockIndex].endLine - 10) {
            style = {
                paddingBottom: Math.max(3, 15 - blocks[blockIndex].endLine + (nodeIndex + 1 + blocks[blockIndex].startLine)) + 'rem',
            };
        }
    }
    return style;
};
const getLanguage = (filePath) => {
    let matches = filePath.match(/\.([a-z]+)$/);
    if (matches && Object.prototype.hasOwnProperty.call(knownExtensions, matches[1])) {
        // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ gemfile: string; rakefile: string; }'.
        return knownExtensions[matches[1]];
    }
    else {
        matches = filePath.toLocaleLowerCase().match(/(?:.*\/)?(.+)$/);
        if (matches && Object.prototype.hasOwnProperty.call(knownFilenames, matches[1])) {
            // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ gemfile: string; rakefile: string; }'.
            return knownFilenames[matches[1]];
        }
    }
};
export const HighlightSyntax = (props) => {
    const { block, blockIndex, blocks, sourceLines, coveredLines, expandedIssues, file, toggleIssueDocs } = props;
    const { t } = useTranslation();
    const lineHeights = useRef({});
    const [, setTick] = useState(0);
    const forceUpdate = () => setTick((tick) => tick + 1);
    const lineNumberContainerStyle = {
        borderRight: '0.0625rem solid #999',
        color: '#999',
        marginRight: '1rem',
        paddingLeft: '1rem',
        paddingRight: '10px',
        textAlign: 'right',
        float: 'left',
    };
    return (_jsx(SyntaxHighlighter, { language: getLanguage(file.filePath || ''), useInlineStyles: false, showLineNumbers: true, startingLineNumber: block.startLine + 1, wrapLines: true, lineProps: (lineNumber) => lineProps(file.filePath, block, lineNumber, t('coveredLine', { count: coveredLines?.[block.startLine + lineNumber] ?? 0 }), lineHeights, forceUpdate, coveredLines), lineNumberStyle: (lineNumber) => lineNumberStyle(block.messages, lineNumber, lineHeights), renderer: ({ rows, stylesheet, useInlineStyles }) => renderer(rows, stylesheet, useInlineStyles, block, blockIndex, expandedIssues, file.filePath, file.messages.length, toggleIssueDocs), lineNumberContainerStyle: lineNumberContainerStyle, PreTag: Pre, realClassName: clsx('report-detail__code', {
            'report-detail__code--ellipsis-top': block.startLine > 0,
            // @ts-expect-error TS2339: Property 'length' does not exist on type 'Record<number, Block>'.
            'report-detail__code--ellipsis-bottom': blockIndex === blocks.length - 1 && block.endLine < sourceLines.length,
        }), realStyle: realStyle(expandedIssues, blocks, blockIndex), children: Array.from({ length: block.endLine - block.startLine }, (value, key) => key + block.startLine)
            .map((lineNumber) => sourceLines[lineNumber])
            .join('\n') }, blockIndex));
};
